import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import MasterSpinner from "../../components/Master/MasterSpinner";
import MasterProfileModal from "../../components/Master/MasterProfileModal";
import { isMasterUser, isMasterAllowedPath, MASTER_HOME } from "../../helpers/masterUser";
import { applyHtmlTheme, clearHtmlTheme } from "../../helpers/syncHtmlTheme";
import { i18n } from "../../translate/i18n";
import MasterSidebar from "./MasterSidebar";
import MasterTopbar from "./MasterTopbar";
import "./master-shell.css";
import "./master.css";

function MasterLayout({ children }) {
	const history = useHistory();
	const location = useLocation();
	const { user, loading, handleLogout } = useContext(AuthContext);
	const { darkMode, setDarkMode } = useContext(ThemeContext);
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(
		typeof window !== "undefined" && window.innerWidth < 1024
	);

	useEffect(() => {
		if (loading) return;
		if (!isMasterUser(user)) {
			history.replace("/");
		}
	}, [user, loading, history]);

	useEffect(() => {
		if (loading || !isMasterUser(user)) return;
		if (!isMasterAllowedPath(location.pathname)) {
			history.replace(MASTER_HOME);
		}
	}, [location.pathname, user, loading, history]);

	useEffect(() => {
		const onResize = () => {
			const mobile = window.innerWidth < 1024;
			setIsMobile(mobile);
			if (!mobile) setMobileMenuOpen(false);
		};
		onResize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		setMobileMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		applyHtmlTheme(darkMode);
		return () => clearHtmlTheme();
	}, [darkMode]);

	if (loading) {
		return (
			<div className="tailwick-master flex min-h-screen items-center justify-center" data-theme="light">
				<MasterSpinner />
			</div>
		);
	}

	if (!isMasterUser(user)) {
		return null;
	}

	const shellClass = [
		"master-shell",
		mobileMenuOpen && isMobile ? "sidenav-enable" : "",
	].filter(Boolean).join(" ");

	return (
		<div
			className={`tailwick-master ${darkMode ? "dark" : ""}`}
			data-theme={darkMode ? "dark" : "light"}
			data-sidenav-size={isMobile ? "offcanvas" : "default"}
		>
			<div className={shellClass}>
				{isMobile && mobileMenuOpen && (
					<button
						type="button"
						className="master-shell-overlay"
						aria-label={i18n.t("master.layout.closeMenu")}
						onClick={() => setMobileMenuOpen(false)}
					/>
				)}

				<MasterSidebar />

				<div className="page-content flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
					<MasterTopbar
						onToggleSidenav={() => setMobileMenuOpen((v) => !v)}
						darkMode={darkMode}
						onToggleDarkMode={() => setDarkMode(!darkMode)}
						onOpenAccount={() => setUserModalOpen(true)}
						onLogout={handleLogout}
					/>
					<main className="flex-grow w-full overflow-y-auto overflow-x-hidden px-6 py-6">
						{children}
					</main>
				</div>
			</div>

			<MasterProfileModal
				open={userModalOpen}
				onClose={() => setUserModalOpen(false)}
				userId={user?.id}
			/>
		</div>
	);
}

export default MasterLayout;
